import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { icons } from "lucide-react";

// ─── Lucide name conversion helpers ──────────────────────────────────────────
//
// The iframe sends iconContent as a Lucide CSS class: "lucide-file-text"
// IconSelect expects a PascalCase icon name:          "FileText"
// handleIconChange must receive the CSS class back:   "lucide-file-text"
//
// These two functions convert between the two formats.

/**
 * "lucide-file-text"  →  "FileText"
 */
export const lucideClassToIconName = (lucideClass) => {
  if (!lucideClass || typeof lucideClass !== "string") return null;
  const withoutPrefix = lucideClass.replace(/^lucide-/, "");
  return withoutPrefix
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
};

/**
 * "FileText"  →  "lucide-file-text"
 */
export const iconNameToLucideClass = (iconName) => {
  if (!iconName || typeof iconName !== "string") return null;
  const kebab = iconName
    .replace(/([A-Z])/g, (match, letter, offset) =>
      offset === 0 ? letter.toLowerCase() : "-" + letter.toLowerCase()
    );
  return "lucide-" + kebab;
};

/**
 * getCurrentIconName
 *
 * Reads the current icon identity from a selectedElement object
 * (as sent by useComponentCommunication via SELECT_COMPONENT).
 *
 * Handles all three formats that iconContent can arrive in:
 *   "lucide-file-text"  → converts to "FileText"
 *   "FileText"          → returns as-is
 *   "<svg ...>"         → returns null (can't map raw SVG to a named icon)
 *
 * Usage in PropertiesPanel:
 *   const currentIcon = getCurrentIconName(selectedElement);
 *   <IconSelect value={currentIcon} onChange={...} />
 */
export const getCurrentIconName = (selectedElement) => {
  if (!selectedElement) return null;
  if (selectedElement.tag !== "svg" && selectedElement.type !== "icon") return null;

  const { iconContent, className } = selectedElement;

  // Try iconContent first
  if (iconContent) {
    if (iconContent.trim().startsWith("<svg")) return null;          // raw SVG — no name
    if (iconContent.startsWith("lucide-")) return lucideClassToIconName(iconContent);
    return iconContent;                                               // already a name
  }

  // Fallback: derive from className  e.g. "lucide lucide-file-text"
  if (className) {
    const lucideClass = className.split(/\s+/).find((c) => c.startsWith("lucide-"));
    if (lucideClass) return lucideClassToIconName(lucideClass);
  }

  return null;
};

// ─── Category definitions ─────────────────────────────────────────────────────

const CATEGORIES = {
  All: null,
  Arrows:  ["Arrow", "Chevron", "Move", "Navigate"],
  Media:   ["Play", "Pause", "Volume", "Music", "Video", "Camera", "Film", "Mic"],
  Files:   ["File", "Folder", "Save", "Upload", "Download", "Archive", "Clipboard"],
  UI:      ["Search", "Settings", "Filter", "Menu", "Grid", "List", "Layout", "Sidebar"],
  People:  ["User", "Users", "Heart", "Star", "Bell", "Mail", "Message", "Phone"],
  Nature:  ["Sun", "Moon", "Cloud", "Wind", "Leaf", "Flame", "Droplet", "Snowflake"],
};

const matchesCategory = (name, keywords) => {
  if (!keywords) return true;
  return keywords.some((kw) => name.toLowerCase().includes(kw.toLowerCase()));
};

// ─── Component ────────────────────────────────────────────────────────────────
//
// Props:
//   value     {string|null}  — current icon name in PascalCase ("FileText")
//                              pass getCurrentIconName(selectedElement) here
//   onChange  {function}     — called with the new icon name in PascalCase
//                              wrap with iconNameToLucideClass() before calling
//                              handleIconChange() in your PropertiesPanel

export default function IconSelect({ value, onChange }) {
  const [open, setOpen]                   = useState(false);
  const [search, setSearch]               = useState("");
  const [selected, setSelected]           = useState(value || null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [hovered, setHovered]             = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [visibleCount, setVisibleCount]   = useState(280);

  const inputRef    = useRef(null);
  const wrapperRef  = useRef(null);
  const triggerRef  = useRef(null);
  const dropdownRef = useRef(null);

  // Keep internal selection in sync when parent passes a new value
  useEffect(() => {
    setSelected(value || null);
  }, [value]);

  const computeDropdownPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const dropdownWidth = 340;
    const spaceRight = window.innerWidth - rect.left;
    const left = spaceRight < dropdownWidth
      ? Math.max(8, rect.right - dropdownWidth)
      : rect.left;

    // Use fixed positioning, so rect is already viewport-relative.
    return { top: rect.bottom + 6, left };
  }, []);

  // Calculate dropdown position before paint to avoid flashing at (0,0)
  useLayoutEffect(() => {
    if (!open) return;
    const next = computeDropdownPosition();
    if (next) setDropdownPosition(next);
  }, [open, computeDropdownPosition]);

  // Keep dropdown aligned on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const next = computeDropdownPosition();
      if (next) setDropdownPosition(next);
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, computeDropdownPosition]);

  // Reset position when closing so next open always recomputes before render
  useEffect(() => {
    if (!open) setDropdownPosition(null);
  }, [open]);

  const iconNames = useMemo(() => Object.keys(icons), []);

  const filtered = useMemo(() => {
    const catKeywords = CATEGORIES[activeCategory];
    return iconNames.filter((name) => {
      const matchesSearch = name.toLowerCase().includes(search.toLowerCase());
      const matchesCat    = matchesCategory(name, catKeywords);
      return matchesSearch && matchesCat;
    });
  }, [search, activeCategory, iconNames]);

  const displayedIcons = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Reset icon batch on open or filter change
  useEffect(() => {
    if (open) setVisibleCount(280);
  }, [open, search, activeCategory]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      const insideTrigger  = wrapperRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const SelectedIcon = selected ? icons[selected] : null;

  const handleSelect = (name) => {
    setSelected(name);
    setOpen(false);
    setHovered(null);
    if (onChange) onChange(name);    // parent receives PascalCase name
  };

  const handleClear = () => {
    setSelected(null);
    if (onChange) onChange(null);
  };

  const handleGridScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom) {
      setVisibleCount((prev) => Math.min(prev + 280, filtered.length));
    }
  };

  return (
    <>
      <style>{`
        .isp-root * { box-sizing: border-box; margin: 0; padding: 0; }

        .isp-root {
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          width: 280px;
          position: relative;
        }

        .isp-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0 14px;
          height: 44px;
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 10px;
          cursor: pointer;
          transition: border-color 0.15s, box-shadow 0.15s;
          color: #18181b;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: -0.01em;
        }
        .isp-trigger:hover {
          border-color: #a1a1aa;
          box-shadow: 0 0 0 3px rgba(0,0,0,0.04);
        }
        .isp-trigger.open {
          border-color: #71717a;
          box-shadow: 0 0 0 3px rgba(0,0,0,0.06);
        }

        .isp-trigger-icon {
          width: 26px;
          height: 26px;
          border-radius: 6px;
          background: #f4f4f5;
          border: 1px solid #e4e4e7;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #52525b;
          flex-shrink: 0;
        }
        .isp-trigger-placeholder { color: #a1a1aa; flex: 1; }
        .isp-trigger-label { flex: 1; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 13px; color: #3f3f46; }
        .isp-trigger-caret {
          color: #a1a1aa;
          transition: transform 0.2s;
          flex-shrink: 0;
        }
        .isp-trigger.open .isp-trigger-caret { transform: rotate(180deg); }

        .isp-dropdown {
          position: fixed;
          width: 340px;
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 14px;
          z-index: 10000;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04);
          animation: dropIn 0.15s cubic-bezier(0.16,1,0.3,1);
        }
        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .isp-search-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-bottom: 1px solid #f4f4f5;
        }
        .isp-search-icon { color: #a1a1aa; flex-shrink: 0; }
        .isp-search-input {
          flex: 1;
          background: none;
          border: none;
          outline: none;
          color: #18181b;
          font-size: 13px;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          font-weight: 500;
          caret-color: #71717a;
        }
        .isp-search-input::placeholder { color: #d4d4d8; }
        .isp-search-count {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 11px;
          color: #d4d4d8;
          flex-shrink: 0;
        }

        .isp-cats {
          display: flex;
          gap: 4px;
          padding: 8px 10px;
          border-bottom: 1px solid #f4f4f5;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .isp-cats::-webkit-scrollbar { display: none; }
        .isp-cat {
          padding: 3px 9px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          color: #a1a1aa;
          background: transparent;
          border: 1px solid transparent;
          transition: all 0.12s;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          letter-spacing: 0.01em;
        }
        .isp-cat:hover { color: #71717a; border-color: #e4e4e7; background: #fafafa; }
        .isp-cat.active { color: #18181b; background: #f4f4f5; border-color: #e4e4e7; }

        .isp-grid-wrap {
          height: 240px;
          overflow-y: auto;
          padding: 10px;
          scrollbar-width: thin;
          scrollbar-color: #e4e4e7 transparent;
        }
        .isp-grid-wrap::-webkit-scrollbar { width: 4px; }
        .isp-grid-wrap::-webkit-scrollbar-thumb { background: #e4e4e7; border-radius: 4px; }

        .isp-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }

        .isp-icon-cell {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 7px;
          cursor: pointer;
          color: #000000;
          border: 1px solid transparent;
          transition: all 0.1s;
        }
        .isp-icon-cell:hover {
          background: #f4f4f5;
          color: #18181b;
          border-color: #e4e4e7;
        }
        .isp-icon-cell.selected {
          background: #18181b;
          color: #fff;
          border-color: #18181b;
        }

        .isp-tooltip {
          position: fixed;
          background: #18181b;
          border: 1px solid #3f3f46;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 11px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          color: #e4e4e7;
          white-space: nowrap;
          pointer-events: none;
          z-index: 99999;
          transform: translateX(-50%);
        }

        .isp-empty {
          height: 240px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .isp-empty-text { font-size: 13px; color: #d4d4d8; }

        .isp-footer {
          border-top: 1px solid #f4f4f5;
          padding: 8px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #fafafa;
        }
        .isp-footer-selected {
          display: flex;
          align-items: center;
          gap: 7px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 12px;
          color: #a1a1aa;
        }
        .isp-footer-selected span { color: #52525b; }
        .isp-clear-btn {
          font-size: 11px;
          color: #a1a1aa;
          cursor: pointer;
          background: none;
          border: 1px solid #e4e4e7;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          padding: 3px 8px;
          border-radius: 5px;
          transition: all 0.1s;
        }
        .isp-clear-btn:hover { background: #f4f4f5; color: #52525b; border-color: #d4d4d8; }
      `}</style>

      <div className="isp-root" ref={wrapperRef}>
        {/* ── Trigger button ─────────────────────────────────────────────── */}
        <div
          ref={triggerRef}
          className={`isp-trigger ${open ? "open" : ""}`}
          onClick={() => {
            setOpen((v) => {
              const nextOpen = !v;
              if (nextOpen) {
                const nextPos = computeDropdownPosition();
                if (nextPos) setDropdownPosition(nextPos);
              }
              return nextOpen;
            });
          }}
        >
          {SelectedIcon ? (
            <div className="isp-trigger-icon">
              <SelectedIcon size={14} strokeWidth={1.75} />
            </div>
          ) : (
            <div className="isp-trigger-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
          )}

          {selected ? (
            <span className="isp-trigger-label">{selected}</span>
          ) : (
            <span className="isp-trigger-placeholder">Choose an icon</span>
          )}

          <svg className="isp-trigger-caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>

        {/* ── Dropdown (portal) ──────────────────────────────────────────── */}
        {open && dropdownPosition && createPortal(
          <div
            ref={dropdownRef}
            className="isp-dropdown"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            {/* Search */}
            <div className="isp-search-row">
              <svg className="isp-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                ref={inputRef}
                className="isp-search-input"
                placeholder="Search icons…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="isp-search-count">{filtered.length}</span>
            </div>

            {/* Category tabs */}
            <div className="isp-cats">
              {Object.keys(CATEGORIES).map((cat) => (
                <button
                  key={cat}
                  className={`isp-cat ${activeCategory === cat ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Icon grid */}
            {filtered.length === 0 ? (
              <div className="isp-empty">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d4d4d8" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <span className="isp-empty-text">No icons found</span>
              </div>
            ) : (
              <div className="isp-grid-wrap" onScroll={handleGridScroll}>
                <div className="isp-grid">
                  {displayedIcons.map((name) => {
                    const Icon = icons[name];
                    return (
                      <div
                        key={name}
                        className={`isp-icon-cell ${selected === name ? "selected" : ""}`}
                        title={name}
                        onMouseEnter={(e) =>
                          setHovered({ name, rect: e.currentTarget.getBoundingClientRect() })
                        }
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => handleSelect(name)}
                      >
                        <Icon size={16} strokeWidth={1.75} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="isp-footer">
              <div className="isp-footer-selected">
                {selected ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#52525b" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>{selected}</span>
                  </>
                ) : (
                  <span style={{ color: "#d4d4d8" }}>None selected</span>
                )}
              </div>
              {selected && (
                <button className="isp-clear-btn" onClick={handleClear}>
                  Clear
                </button>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* ── Tooltip ───────────────────────────────────────────────────── */}
        {hovered && (
          <div
            className="isp-tooltip"
            style={{
              top:  hovered.rect.top - 32,
              left: hovered.rect.left + hovered.rect.width / 2,
            }}
          >
            {hovered.name}
          </div>
        )}
      </div>
    </>
  );
}